'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import {
  Pencil,
  Eraser,
  Circle,
  Square,
  Minus,
  Type,
  MousePointer2,
  Trash2,
  Undo2,
  Redo2,
  Download,
  ZoomIn,
  ZoomOut,
  Move,
  Palette,
  Presentation,
  X,
  Maximize2,
  Minimize2
} from 'lucide-react';
import type { DocumentPresentationState } from '@/app/call/room/types';
import { useLandscapeFullscreen } from '@/app/call/room/hooks/useLandscapeFullscreen';

const PresentationViewer = dynamic(
  () => import('@/app/call/room/components/PresentationViewer'),
  { ssr: false }
);

// Shared logical canvas — all participants draw in the same 1600x900 coordinate
// space so strokes line up across desktop and mobile. The canvas element still
// fills its container; the board is letterboxed (fit) inside it.
const LOGICAL_BOARD_WIDTH = 1600;
const LOGICAL_BOARD_HEIGHT = 900;

interface Point {
  x: number;
  y: number;
}

export interface DrawingElement {
  id: string;
  type: 'pen' | 'line' | 'rectangle' | 'circle' | 'text' | 'eraser';
  points: Point[];
  color: string;
  strokeWidth: number;
  text?: string;
  userId: string;
  userName: string;
  timestamp: number;
}

interface WhiteboardProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  roomId: string;
  onSendDrawing?: (element: DrawingElement) => void;
  onReceiveDrawing?: (element: DrawingElement) => void;
  onClear?: () => void;
  remoteElements?: DrawingElement[];
  /** When true, only pan/zoom/view; no drawing or clear for others' session. */
  readOnly?: boolean;
  /** Shown in header when following another participant's board. */
  followerPresenterName?: string | null;
  /** Active document presentation synced over the data channel. */
  presentation?: DocumentPresentationState | null;
  /** Whether the local participant is the document presenter. */
  isPresentationPresenter?: boolean;
  /** True while a share link is being created for the picked file. */
  presentingFile?: boolean;
  /** Presenter-controlled page number (pdf). */
  presentationPage?: number;
  presentationNavSeq?: number;
  presentationScrollRatio?: number | null;
  /** Whether the local user can create share links (signed-in). */
  canPickFiles?: boolean;
  onOpenPresentPicker?: () => void;
  onPresentationNavigate?: (direction: 'next' | 'prev' | 'goto', page?: number) => void;
  onPresentationScroll?: (ratio: number) => void;
  onStopPresentation?: () => void;
}

const COLORS = [
  '#000000', // Black
  '#FFFFFF', // White
  '#EF4444', // Red
  '#F97316', // Orange
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6B7280', // Gray
];

const STROKE_WIDTHS = [2, 4, 6, 8, 12];

type Tool = 'select' | 'pen' | 'line' | 'rectangle' | 'circle' | 'text' | 'eraser' | 'pan';

export default function CallWhiteboard({
  isOpen,
  onClose,
  userId,
  userName,
  roomId,
  onSendDrawing,
  onClear,
  remoteElements = [],
  readOnly = false,
  followerPresenterName = null,
  presentation = null,
  isPresentationPresenter = false,
  presentingFile = false,
  presentationPage = 1,
  presentationNavSeq = 0,
  presentationScrollRatio = null,
  canPickFiles = false,
  onOpenPresentPicker,
  onPresentationNavigate,
  onPresentationScroll,
  onStopPresentation,
}: WhiteboardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Rotate to landscape → pin the board fullscreen so the shared logical
  // canvas fills a phone's screen (portrait letterboxes to fit width).
  const { isFullscreen: isBrowserFullscreen } = useLandscapeFullscreen(overlayRef);
  
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [textDraft, setTextDraft] = useState('');
  const textAnchorRef = useRef<Point | null>(null);
  const [clearModalOpen, setClearModalOpen] = useState(false);
  
  // Tool state
  const [selectedTool, setSelectedTool] = useState<Tool>('pen');
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // Canvas state
  const [isDrawing, setIsDrawing] = useState(false);
  const [elements, setElements] = useState<DrawingElement[]>([]);
  const [currentElement, setCurrentElement] = useState<DrawingElement | null>(null);
  const [history, setHistory] = useState<DrawingElement[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // View state
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState<Point | null>(null);
  
  // Combine local and remote elements
  const allElements = useMemo(() => {
    return [...elements, ...remoteElements].sort((a, b) => a.timestamp - b.timestamp);
  }, [elements, remoteElements]);
  
  // Get canvas context
  const getContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d');
  }, []);
  
  // Logical-board view transform: fit the shared 1600x900 board inside the
  // canvas element (contain), then apply the user's zoom/pan on top.
  const getViewTransform = useCallback(() => {
    const canvas = canvasRef.current;
    const cw = canvas?.width || 0;
    const ch = canvas?.height || 0;
    if (!cw || !ch) return { scale: 1, offsetX: 0, offsetY: 0 };
    const fit = Math.min(cw / LOGICAL_BOARD_WIDTH, ch / LOGICAL_BOARD_HEIGHT) * scale;
    return {
      scale: fit,
      offsetX: (cw - LOGICAL_BOARD_WIDTH * fit) / 2 + offset.x,
      offsetY: (ch - LOGICAL_BOARD_HEIGHT * fit) / 2 + offset.y,
    };
  }, [scale, offset]);

  // Convert screen coordinates to shared logical-board coordinates
  const screenToCanvas = useCallback((clientX: number, clientY: number): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const t = getViewTransform();
    const pxScale = rect.width > 0 ? canvas.width / rect.width : 1;
    const cx = (clientX - rect.left) * pxScale;
    const cy = (clientY - rect.top) * pxScale;
    return {
      x: Math.min(Math.max((cx - t.offsetX) / t.scale, 0), LOGICAL_BOARD_WIDTH),
      y: Math.min(Math.max((cy - t.offsetY) / t.scale, 0), LOGICAL_BOARD_HEIGHT),
    };
  }, [getViewTransform]);
  
  // Draw all elements
  const drawElements = useCallback(() => {
    const ctx = getContext();
    const canvas = canvasRef.current;
    if (!ctx || !canvas) return;
    
    // Backdrop + shared logical board rect (letterboxed inside the canvas)
    const t = getViewTransform();
    ctx.fillStyle = '#1F2937';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const boardX = t.offsetX;
    const boardY = t.offsetY;
    const boardW = LOGICAL_BOARD_WIDTH * t.scale;
    const boardH = LOGICAL_BOARD_HEIGHT * t.scale;

    ctx.save();
    ctx.beginPath();
    ctx.rect(boardX, boardY, boardW, boardH);
    ctx.clip();

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(boardX, boardY, boardW, boardH);

    // Apply transformations — element points live in 1600x900 logical space
    ctx.translate(t.offsetX, t.offsetY);
    ctx.scale(t.scale, t.scale);
    ctx.lineWidth = 1;
    
    // Draw each element
    allElements.forEach(element => {
      ctx.strokeStyle = element.color;
      ctx.fillStyle = element.color;
      ctx.lineWidth = element.strokeWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      switch (element.type) {
        case 'pen':
        case 'eraser':
          if (element.points.length < 2) break;
          ctx.beginPath();
          if (element.type === 'eraser') {
            ctx.strokeStyle = '#FFFFFF';
          }
          ctx.moveTo(element.points[0].x, element.points[0].y);
          for (let i = 1; i < element.points.length; i++) {
            ctx.lineTo(element.points[i].x, element.points[i].y);
          }
          ctx.stroke();
          break;
          
        case 'line':
          if (element.points.length < 2) break;
          ctx.beginPath();
          ctx.moveTo(element.points[0].x, element.points[0].y);
          ctx.lineTo(element.points[1].x, element.points[1].y);
          ctx.stroke();
          break;
          
        case 'rectangle':
          if (element.points.length < 2) break;
          const rectWidth = element.points[1].x - element.points[0].x;
          const rectHeight = element.points[1].y - element.points[0].y;
          ctx.strokeRect(element.points[0].x, element.points[0].y, rectWidth, rectHeight);
          break;
          
        case 'circle':
          if (element.points.length < 2) break;
          const radius = Math.sqrt(
            Math.pow(element.points[1].x - element.points[0].x, 2) +
            Math.pow(element.points[1].y - element.points[0].y, 2)
          );
          ctx.beginPath();
          ctx.arc(element.points[0].x, element.points[0].y, radius, 0, Math.PI * 2);
          ctx.stroke();
          break;
          
        case 'text':
          if (!element.text) break;
          ctx.font = `${element.strokeWidth * 4}px sans-serif`;
          ctx.fillText(element.text, element.points[0].x, element.points[0].y);
          break;
      }
    });
    
    // Draw current element if drawing
    if (currentElement) {
      ctx.strokeStyle = currentElement.color;
      ctx.fillStyle = currentElement.color;
      ctx.lineWidth = currentElement.strokeWidth;
      
      switch (currentElement.type) {
        case 'pen':
        case 'eraser':
          if (currentElement.points.length >= 2) {
            ctx.beginPath();
            if (currentElement.type === 'eraser') {
              ctx.strokeStyle = '#FFFFFF';
            }
            ctx.moveTo(currentElement.points[0].x, currentElement.points[0].y);
            for (let i = 1; i < currentElement.points.length; i++) {
              ctx.lineTo(currentElement.points[i].x, currentElement.points[i].y);
            }
            ctx.stroke();
          }
          break;
          
        case 'line':
          if (currentElement.points.length >= 2) {
            ctx.beginPath();
            ctx.moveTo(currentElement.points[0].x, currentElement.points[0].y);
            ctx.lineTo(currentElement.points[1].x, currentElement.points[1].y);
            ctx.stroke();
          }
          break;
          
        case 'rectangle':
          if (currentElement.points.length >= 2) {
            const w = currentElement.points[1].x - currentElement.points[0].x;
            const h = currentElement.points[1].y - currentElement.points[0].y;
            ctx.strokeRect(currentElement.points[0].x, currentElement.points[0].y, w, h);
          }
          break;
          
        case 'circle':
          if (currentElement.points.length >= 2) {
            const r = Math.sqrt(
              Math.pow(currentElement.points[1].x - currentElement.points[0].x, 2) +
              Math.pow(currentElement.points[1].y - currentElement.points[0].y, 2)
            );
            ctx.beginPath();
            ctx.arc(currentElement.points[0].x, currentElement.points[0].y, r, 0, Math.PI * 2);
            ctx.stroke();
          }
          break;
      }
    }
    
    ctx.restore();

    // Board boundary so the shared canvas edge is visible on all screens
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 2;
    ctx.strokeRect(boardX, boardY, boardW, boardH);
  }, [allElements, currentElement, getContext, getViewTransform]);
  
  // Redraw on changes
  useEffect(() => {
    drawElements();
  }, [drawElements]);
  
  // Resize canvas to fit container
  useEffect(() => {
    if (!isOpen) return;
    
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      
      // Make sure container has dimensions before setting canvas size
      const width = container.clientWidth || container.offsetWidth;
      const height = container.clientHeight || container.offsetHeight;
      
      if (width > 0 && height > 0) {
        canvas.width = width;
        canvas.height = height;
        drawElements();
      }
    };
    
    // Initial resize with a small delay to ensure container is rendered
    const initialResize = setTimeout(resizeCanvas, 50);
    
    // Also resize immediately in case it works
    resizeCanvas();
    
    window.addEventListener('resize', resizeCanvas);
    return () => {
      clearTimeout(initialResize);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [drawElements, isOpen]);
  
  const commitTextElement = useCallback(
    (point: Point, text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const newElement: DrawingElement = {
        id: `${userId}-${Date.now()}`,
        type: 'text',
        points: [point],
        color: selectedColor,
        strokeWidth,
        text: trimmed,
        userId,
        userName,
        timestamp: Date.now(),
      };
      setElements(prev => [...prev, newElement]);
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push([...elements, newElement]);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
      onSendDrawing?.(newElement);
    },
    [userId, userName, selectedColor, strokeWidth, history, historyIndex, elements, onSendDrawing]
  );
  
  // Handle mouse/touch down
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const point = screenToCanvas(e.clientX, e.clientY);

      if (readOnly) {
        if (selectedTool === 'pan' || e.buttons === 4) {
          setIsPanning(true);
          setLastPanPoint({ x: e.clientX, y: e.clientY });
        }
        return;
      }

      if (selectedTool === 'pan' || e.buttons === 4) {
        setIsPanning(true);
        setLastPanPoint({ x: e.clientX, y: e.clientY });
        return;
      }

      if (selectedTool === 'select') return;

      setIsDrawing(true);

      const newElement: DrawingElement = {
        id: `${userId}-${Date.now()}`,
        type: selectedTool as DrawingElement['type'],
        points: [point],
        color: selectedColor,
        strokeWidth,
        userId,
        userName,
        timestamp: Date.now(),
      };

      if (selectedTool === 'text') {
        textAnchorRef.current = point;
        setTextDraft('');
        setTextModalOpen(true);
        setIsDrawing(false);
        return;
      }

      setCurrentElement(newElement);
    },
    [readOnly, selectedTool, screenToCanvas, userId, userName, selectedColor, strokeWidth]
  );
  
  // Handle mouse/touch move
  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (isPanning && lastPanPoint) {
      const dx = e.clientX - lastPanPoint.x;
      const dy = e.clientY - lastPanPoint.y;
      setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setLastPanPoint({ x: e.clientX, y: e.clientY });
      return;
    }
    
    if (!isDrawing || !currentElement) return;
    
    const point = screenToCanvas(e.clientX, e.clientY);
    
    if (currentElement.type === 'pen' || currentElement.type === 'eraser') {
      setCurrentElement(prev => ({
        ...prev!,
        points: [...prev!.points, point],
      }));
    } else {
      setCurrentElement(prev => ({
        ...prev!,
        points: [prev!.points[0], point],
      }));
    }
  }, [isPanning, lastPanPoint, isDrawing, currentElement, screenToCanvas]);
  
  // Handle mouse/touch up
  const handlePointerUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      setLastPanPoint(null);
      return;
    }
    
    if (!isDrawing || !currentElement) return;
    
    setIsDrawing(false);
    
    // Add to elements
    setElements(prev => [...prev, currentElement]);
    
    // Save to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...elements, currentElement]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    
    // Send to other participants
    onSendDrawing?.(currentElement);
    
    setCurrentElement(null);
  }, [isPanning, isDrawing, currentElement, elements, history, historyIndex, onSendDrawing]);
  
  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(prev => prev - 1);
      setElements(history[historyIndex - 1]);
    }
  }, [historyIndex, history]);
  
  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(prev => prev + 1);
      setElements(history[historyIndex + 1]);
    }
  }, [historyIndex, history]);
  
  const runClearAll = useCallback(() => {
    setElements([]);
    const newHistory = [...history, []];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    onClear?.();
    setClearModalOpen(false);
  }, [history, onClear]);

  // Clear all (presenters only)
  const handleClear = useCallback(() => {
    setClearModalOpen(true);
  }, []);
  
  // Download as image
  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `whiteboard-${roomId}-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [roomId]);
  
  // Zoom controls
  const handleZoomIn = () => setScale(prev => Math.min(prev * 1.2, 5));
  const handleZoomOut = () => setScale(prev => Math.max(prev / 1.2, 0.2));
  const handleResetZoom = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };
  
  if (!isOpen) return null;
  
  const isFollower = readOnly && followerPresenterName;

  return (
    <div
      ref={overlayRef}
      className={`fixed ${isFullscreen || isBrowserFullscreen ? 'inset-0' : 'inset-4 lg:inset-8'} z-[60] flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-2xl overflow-hidden`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col gap-0.5 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white">Whiteboard</h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {allElements.length} stroke{allElements.length !== 1 ? 's' : ''}
            </span>
          </div>
          {isFollower ? (
            <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium truncate">
              Viewing {followerPresenterName}&rsquo;s board — you can pan and zoom only
            </p>
          ) : (
            !readOnly && (
              <p className="text-xs text-gray-500 dark:text-gray-400">You are presenting to this call</p>
            )
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Zoom controls */}
          <div className="flex items-center gap-1 mr-2">
            <button
              onClick={handleZoomOut}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Zoom out"
            >
              <ZoomOut size={18} />
            </button>
            <button
              onClick={handleResetZoom}
              className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Reset zoom"
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              onClick={handleZoomIn}
              className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Zoom in"
            >
              <ZoomIn size={18} />
            </button>
          </div>
          
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
          
          <button
            onClick={onClose}
            className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            title="Close whiteboard"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Toolbar — hidden while a document is being presented */}
      {!presentation ? (!readOnly ? (
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-850 border-b border-gray-200 dark:border-gray-700">
        {/* Tool selection */}
        <div className="flex items-center gap-1 p-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <button
            onClick={() => setSelectedTool('select')}
            className={`p-2 rounded ${selectedTool === 'select' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Select"
          >
            <MousePointer2 size={18} />
          </button>
          <button
            onClick={() => setSelectedTool('pen')}
            className={`p-2 rounded ${selectedTool === 'pen' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Pen"
          >
            <Pencil size={18} />
          </button>
          <button
            onClick={() => setSelectedTool('line')}
            className={`p-2 rounded ${selectedTool === 'line' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Line"
          >
            <Minus size={18} />
          </button>
          <button
            onClick={() => setSelectedTool('rectangle')}
            className={`p-2 rounded ${selectedTool === 'rectangle' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Rectangle"
          >
            <Square size={18} />
          </button>
          <button
            onClick={() => setSelectedTool('circle')}
            className={`p-2 rounded ${selectedTool === 'circle' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Circle"
          >
            <Circle size={18} />
          </button>
          <button
            onClick={() => setSelectedTool('text')}
            className={`p-2 rounded ${selectedTool === 'text' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Text"
          >
            <Type size={18} />
          </button>
          <button
            onClick={() => setSelectedTool('eraser')}
            className={`p-2 rounded ${selectedTool === 'eraser' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Eraser"
          >
            <Eraser size={18} />
          </button>
          <button
            onClick={() => setSelectedTool('pan')}
            className={`p-2 rounded ${selectedTool === 'pan' ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            title="Pan"
          >
            <Move size={18} />
          </button>
        </div>
        
        <div className="w-px h-8 bg-gray-300 dark:bg-gray-600" />
        
        {/* Color picker */}
        <div className="relative">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-sm"
            title="Color"
          >
            <div 
              className="w-5 h-5 rounded border border-gray-300 dark:border-gray-600" 
              style={{ backgroundColor: selectedColor }}
            />
            <Palette size={18} className="text-gray-600 dark:text-gray-400" />
          </button>
          
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-2 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
              <div className="grid grid-cols-5 gap-1">
                {COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => {
                      setSelectedColor(color);
                      setShowColorPicker(false);
                    }}
                    className={`w-6 h-6 rounded border-2 ${selectedColor === color ? 'border-blue-500' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Stroke width */}
        <div className="flex items-center gap-1 p-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          {STROKE_WIDTHS.map(width => (
            <button
              key={width}
              onClick={() => setStrokeWidth(width)}
              className={`p-2 rounded ${strokeWidth === width ? 'bg-blue-500 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
              title={`${width}px`}
            >
              <div 
                className="rounded-full bg-current" 
                style={{ width: Math.min(width + 4, 16), height: Math.min(width + 4, 16) }}
              />
            </button>
          ))}
        </div>
        
        <div className="w-px h-8 bg-gray-300 dark:bg-gray-600" />
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleUndo}
            disabled={historyIndex === 0}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            title="Undo"
          >
            <Undo2 size={18} />
          </button>
          <button
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            title="Redo"
          >
            <Redo2 size={18} />
          </button>
          <button
            onClick={handleClear}
            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            title="Clear all"
          >
            <Trash2 size={18} />
          </button>
          <button
            onClick={handleDownload}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            title="Download as image"
          >
            <Download size={18} />
          </button>
          {canPickFiles && onOpenPresentPicker && (
            <button
              onClick={onOpenPresentPicker}
              disabled={presentingFile}
              className="flex items-center gap-1.5 p-2 text-[var(--theme-primary)] hover:bg-[var(--theme-primary)]/10 rounded disabled:opacity-50"
              title="Present a file from your cloud drive to everyone"
            >
              <Presentation size={18} />
              <span className="text-xs font-medium hidden sm:inline">
                {presentingFile ? 'Opening…' : 'Present file'}
              </span>
            </button>
          )}
        </div>
      </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-850 border-b border-gray-200 dark:border-gray-700">
          <span className="text-xs text-gray-600 dark:text-gray-400 mr-2">Pan to move • scroll wheel zoom is in the toolbar above</span>
          <button
            onClick={handleDownload}
            className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded inline-flex items-center gap-1.5 text-sm"
            title="Download snapshot"
          >
            <Download size={18} />
            Save image
          </button>
        </div>
      )) : null}
      
      {/* Canvas or document presentation */}
      {presentation ? (
        <PresentationViewer
          presentation={presentation}
          isPresenter={isPresentationPresenter}
          navPage={presentationPage}
          navSeq={presentationNavSeq}
          scrollRatio={presentationScrollRatio}
          onNavigate={(dir, page) => onPresentationNavigate?.(dir, page)}
          onScrollRatio={(ratio) => onPresentationScroll?.(ratio)}
          onStop={() => onStopPresentation?.()}
        />
      ) : (
      <div
        ref={containerRef}
        className={`flex-1 overflow-hidden bg-slate-50 dark:bg-gray-950 ${readOnly ? 'cursor-grab active:cursor-grabbing' : 'bg-white cursor-crosshair'}`}
        style={{ touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          className="w-full h-full"
          style={{
            cursor: readOnly
              ? 'grab'
              : selectedTool === 'pan'
                ? 'grab'
                : selectedTool === 'select'
                  ? 'default'
                  : selectedTool === 'text'
                    ? 'text'
                    : 'crosshair'
          }}
        />
      </div>
      )}
      
      {/* Footer with participants info */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-850 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        {isFollower ? (
          <span>
            Watching <span className="text-gray-800 dark:text-gray-200">{followerPresenterName}</span>
            <span className="mx-2">•</span>
            <span>Room: {roomId}</span>
          </span>
        ) : (
          <>
            <span>Drawing as {userName}</span>
            <span className="mx-2">•</span>
            <span>Room: {roomId}</span>
          </>
        )}
      </div>

      {textModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="wb-text-title">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-4 border border-gray-200 dark:border-gray-600">
            <h4 id="wb-text-title" className="font-medium text-gray-900 dark:text-white mb-2">
              Add text
            </h4>
            <input
              type="text"
              value={textDraft}
              onChange={(ev) => setTextDraft(ev.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm mb-3"
              placeholder="Type here…"
              autoFocus
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  ev.preventDefault();
                  if (textAnchorRef.current) commitTextElement(textAnchorRef.current, textDraft);
                  setTextModalOpen(false);
                  textAnchorRef.current = null;
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => {
                  setTextModalOpen(false);
                  textAnchorRef.current = null;
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-500"
                onClick={() => {
                  if (textAnchorRef.current) commitTextElement(textAnchorRef.current, textDraft);
                  setTextModalOpen(false);
                  textAnchorRef.current = null;
                }}
              >
                Place text
              </button>
            </div>
          </div>
        </div>
      )}

      {clearModalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-4 border border-gray-200 dark:border-gray-600">
            <p className="text-gray-900 dark:text-white text-sm mb-4">
              Clear all drawings for everyone in this call? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => setClearModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-500"
                onClick={runClearAll}
              >
                Clear all
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
